<template>
  <div class="welcome-stage">
    <div ref="board" class="welcome-board" :style="boardStyle">
      <!-- floating decorative blobs -->
      <div class="blob blob-top"></div>
      <div class="blob blob-bottom"></div>

      <div class="board-inner">
        <!-- Header. Hidden on the general screen so the big centered logo +
             clinic name below can serve as the hero instead. -->
        <div class="header rise" v-if="!isGeneral">
          <div class="brand">
            <img
              v-if="p.clinicLogoUrl"
              :src="p.clinicLogoUrl"
              class="brand-logo"
              alt="logo"
            />
            <div v-else class="brand-mark">{{ brandInitial }}</div>
            <div>
              <div class="brand-name">{{ clinicName }}</div>
              <div class="brand-tagline">{{ tagline }}</div>
            </div>
          </div>
          <div class="meta">
            <div v-if="p.roomName" class="meta-pill">
              <span class="meta-dot"></span>{{ p.roomName }}
            </div>
            <div v-if="p.doctorName" class="meta-pill">{{ p.doctorName }}</div>
          </div>
        </div>

        <!-- Body (welcome mode, patient selected) -->
        <div class="body" v-if="mode === 'welcome' && hasPatient">
          <!-- Left column -->
          <div class="left rise rise-1">
            <div class="eyebrow"><span class="eyebrow-line"></span>{{ S.welcomeEyebrow }}</div>
            <div ref="greeting" class="greeting">
              {{ S.greetingHello }}
              <span ref="greetingName" class="greeting-name">{{ greetingName }}</span>
            </div>
            <div class="subline" v-if="secondaryName || ageText">
              {{ S.sublineGlad }}
              <span class="subline-strong" v-if="secondaryName">{{ secondaryName }}</span>
              <span v-if="secondaryName && ageText"> · </span>
              <span v-if="ageText">{{ ageText }}</span>
            </div>
            <transition name="fade-swap" mode="out-in">
              <div class="welcome-copy" :key="'copy' + welcomeIndex">
                {{ currentWelcomeLine }}
              </div>
            </transition>

            <div class="spacer"></div>

            <div class="care-tip">
              <div class="care-quote">&ldquo;</div>
              <div>
                <div class="care-label">{{ S.tipLabel }}</div>
                <transition name="fade-swap" mode="out-in">
                  <div class="care-text" :key="'tip' + tipIndex">
                    {{ currentTip }}
                  </div>
                </transition>
              </div>
            </div>
          </div>

          <!-- Right column -->
          <div class="right rise rise-2">
            <!-- Today's treatment -->
            <div class="card" v-if="p.treatmentTitle">
              <div class="card-eyebrow"><span class="dot dot-teal"></span>{{ S.treatmentEyebrow }}</div>
              <div class="card-title">{{ p.treatmentTitle }}</div>
              <div class="card-sub" v-if="p.doctorName">{{ S.treatmentBy }} {{ p.doctorName }}</div>
              <div class="duration-pill" v-if="p.durationMinutes">
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none"
                  stroke="#2f8f99" stroke-width="2" stroke-linecap="round"
                  stroke-linejoin="round">
                  <circle cx="12" cy="12" r="9"></circle>
                  <path d="M12 7v5l3 2"></path>
                </svg>
                <span>{{ durationText }}</span>
              </div>
            </div>

            <!-- Medical alert -->
            <div class="alert-card" v-if="showAlert">
              <div class="alert-head">
                <div class="alert-icon">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none"
                    stroke="#c98a37" stroke-width="2" stroke-linecap="round"
                    stroke-linejoin="round">
                    <path d="M12 3 2 20h20L12 3z"></path>
                    <path d="M12 10v4.5"></path>
                    <circle cx="12" cy="17.6" r="0.7" fill="#c98a37" stroke="none"></circle>
                  </svg>
                </div>
                <div class="alert-title">{{ S.alertTitle }}</div>
              </div>
              <div class="alert-cols">
                <div class="alert-col" v-if="p.allergies">
                  <div class="alert-key">{{ S.allergyKey }}</div>
                  <div class="alert-val">{{ p.allergies }}</div>
                </div>
                <div class="alert-divider" v-if="p.allergies && p.conditions"></div>
                <div class="alert-col" v-if="p.conditions">
                  <div class="alert-key">{{ S.conditionKey }}</div>
                  <div class="alert-val">{{ p.conditions }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Body (welcome mode, general / thankyou screen — no patient). Shown
             when the queue is empty or the doctor navigates to the general slot. -->
        <div class="welcome-general rise" v-else-if="mode === 'welcome'">
          <div class="wg-inner">
            <div class="wg-hello">{{ S.helloEyebrow }}</div>
            <img
              v-if="p.clinicLogoUrl"
              :src="p.clinicLogoUrl"
              class="wg-logo"
              alt="logo"
            />
            <div v-else class="wg-mark">{{ brandInitial }}</div>
            <div class="wg-title">{{ clinicName || S.generalFallbackName }}</div>
            <div class="wg-welcome">{{ tagline }}</div>
            <transition name="fade-swap" mode="out-in">
              <div class="wg-copy" :key="'wg-copy' + welcomeIndex">
                {{ currentWelcomeLine }}
              </div>
            </transition>
            <div class="care-tip wg-tip">
              <div class="care-quote">&ldquo;</div>
              <div>
                <div class="care-label">{{ S.tipLabel }}</div>
                <transition name="fade-swap" mode="out-in">
                  <div class="care-text" :key="'wg-tip' + tipIndex">
                    {{ currentTip }}
                  </div>
                </transition>
              </div>
            </div>
          </div>
        </div>

        <!-- Body (post-op care mode). Treatment is chosen in the gear panel. -->
        <template v-else>
          <div class="po-titlebar">
            <div>
              <div class="po-eyebrow">
                <span class="po-eyebrow-line"></span>{{ S.postOpEyebrow }}
              </div>
              <div class="po-title">{{ activePostOp.label }}</div>
            </div>
            <div class="po-for">
              <div class="po-for-name">
                {{ S.postOpFor }} <span>{{ greetingName }}</span>
              </div>
              <div class="po-for-date" v-if="p.dateText">{{ p.dateText }}</div>
            </div>
          </div>

          <div class="po-grid">
            <!-- ควรปฏิบัติ -->
            <div class="po-card po-card--do">
              <div class="po-card-head">
                <div class="po-card-icon">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none"
                    stroke="#1f7a45" stroke-width="3" stroke-linecap="round"
                    stroke-linejoin="round"><path d="M5 13l4 4L19 7"></path></svg>
                </div>
                <div class="po-card-title">{{ S.doTitle }}</div>
              </div>
              <div class="po-items">
                <div class="po-item" v-for="(item, i) in activePostOp.dos" :key="'do' + i">
                  <svg class="po-item-ic" width="26" height="26" viewBox="0 0 24 24"
                    fill="none" stroke="#2ea05d" stroke-width="3" stroke-linecap="round"
                    stroke-linejoin="round"><path d="M5 13l4 4L19 7"></path></svg>
                  <span>{{ item }}</span>
                </div>
              </div>
            </div>

            <!-- ควรหลีกเลี่ยง -->
            <div class="po-card po-card--avoid">
              <div class="po-card-head">
                <div class="po-card-icon">
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none"
                    stroke="#b53636" stroke-width="3" stroke-linecap="round"
                    stroke-linejoin="round"><path d="M6 6l12 12M18 6L6 18"></path></svg>
                </div>
                <div class="po-card-title">{{ S.avoidTitle }}</div>
              </div>
              <div class="po-items">
                <div class="po-item" v-for="(item, i) in activePostOp.donts" :key="'no' + i">
                  <svg class="po-item-ic" width="24" height="24" viewBox="0 0 24 24"
                    fill="none" stroke="#c93737" stroke-width="3" stroke-linecap="round"
                    stroke-linejoin="round"><path d="M6 6l12 12M18 6L6 18"></path></svg>
                  <span>{{ item }}</span>
                </div>
              </div>
            </div>

            <!-- ข้อควรรู้ -->
            <div class="po-card po-card--note">
              <div class="po-card-head">
                <div class="po-card-icon">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none"
                    stroke="#2f8f99" stroke-width="2.4" stroke-linecap="round"
                    stroke-linejoin="round"><circle cx="12" cy="12" r="9"></circle>
                    <path d="M12 11.5v5"></path>
                    <circle cx="12" cy="7.7" r="1.1" fill="#2f8f99" stroke="none"></circle></svg>
                </div>
                <div class="po-card-title">{{ S.noteTitle }}</div>
              </div>
              <div class="po-items">
                <div class="po-item po-item--note" v-for="(item, i) in activePostOp.notes" :key="'nt' + i">
                  <span class="po-bullet"></span>
                  <span>{{ item }}</span>
                </div>
              </div>
              <div class="po-spacer"></div>
              <div class="po-contact" v-if="p.clinicPhone">
                {{ S.contactPrefix }} <span>{{ p.clinicPhone }}</span>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- Doctor control panel (language / mode / treatment / theme). Hidden from
         patient. -->
    <div ref="panel" class="dc-panel" v-show="panelOpen">
      <div class="dc-section">
        <div class="dc-label">{{ S.panelLang }}</div>
        <div class="dc-seg">
          <button
            class="dc-seg-btn"
            :class="{ 'dc-seg-btn--active': lang === 'th' }"
            @click="setLang('th')"
          >
            ไทย
          </button>
          <button
            class="dc-seg-btn"
            :class="{ 'dc-seg-btn--active': lang === 'en' }"
            @click="setLang('en')"
          >
            EN
          </button>
        </div>
      </div>

      <div class="dc-section">
        <div class="dc-label">{{ S.panelMode }}</div>
        <div class="dc-seg">
          <button
            class="dc-seg-btn"
            :class="{ 'dc-seg-btn--active': mode === 'welcome' }"
            @click="setMode('welcome')"
          >
            {{ S.panelModeWelcome }}
          </button>
          <button
            class="dc-seg-btn"
            :class="{ 'dc-seg-btn--active': mode === 'postop' }"
            @click="setMode('postop')"
          >
            {{ S.panelModePostop }}
          </button>
        </div>
      </div>

      <div class="dc-section">
        <div class="dc-label">{{ S.panelTreatment }}</div>
        <div class="dc-grid">
          <button
            v-for="t in postOpList"
            :key="t.key"
            class="dc-grid-btn"
            :class="{ 'dc-grid-btn--active': mode === 'postop' && t.key === postOpKey }"
            @click="selectPostOp(t.key)"
          >
            {{ t.label[lang] }}
          </button>
        </div>
      </div>

      <div class="dc-section">
        <div class="dc-label">{{ S.panelTheme }}</div>
        <div class="dc-presets">
          <button
            v-for="preset in themePresets"
            :key="preset.key"
            class="dc-preset"
            :class="{ 'dc-preset--active': theme.key === preset.key }"
            :style="{ background: preset.bg, color: preset.text }"
            :title="preset.label"
            @click="selectPreset(preset)"
          >
            {{ preset.label }}
          </button>
        </div>
        <div class="dc-colors">
          <label>{{ S.panelBg }} <input type="color" :value="bgHex" @input="onBgInput" /></label>
          <label>{{ S.panelText }} <input type="color" :value="theme.text" @input="onTextInput" /></label>
        </div>
      </div>
    </div>

    <!-- Gear toggle. Fades out when idle so the patient sees a clean board. -->
    <button
      ref="gear"
      class="dc-gear"
      :class="{ 'dc-gear--idle': ctrlIdle && !panelOpen }"
      :aria-label="S.gearAria"
      @click="togglePanel"
    >
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#fff"
        stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="12" cy="12" r="3"></circle>
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
      </svg>
    </button>

    <!-- Subtle queue navigation (also driven by Up/Down arrow keys). Auto-hides
         with the gear so the patient sees a clean board. Only when a queue exists. -->
    <div
      v-if="queue.length"
      class="dc-nav"
      :class="{ 'dc-nav--idle': ctrlIdle && !panelOpen }"
    >
      <button class="dc-nav-btn" :aria-label="S.navPrev" @click="selectPrev">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#234b50"
          stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M6 15l6-6 6 6"></path>
        </svg>
      </button>
      <button class="dc-nav-btn" :aria-label="S.navNext" @click="selectNext">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#234b50"
          stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M6 9l6 6 6-6"></path>
        </svg>
      </button>
    </div>
  </div>
</template>

<script>
import { readLastQueue, subscribeWelcomeQueue } from "@/utils/welcomeChannel";
import {
  nextSelection,
  prevSelection,
  reconcileSelection,
  nextPostOp,
  prevPostOp,
} from "@/utils/welcomeQueueNav";
import { POSTOP_TREATMENTS, getPostOp } from "@/utils/postOpInstructions";
import { THEME_PRESETS, loadTheme, saveTheme } from "@/utils/welcomeTheme";
import { CARE_TIPS, WELCOME_LINES } from "@/utils/welcomeTips";
import { loadLang, saveLang } from "@/utils/welcomeLang";
import { WELCOME_STRINGS } from "@/utils/welcomeStrings";
import { fitFontSize } from "@/utils/welcomeFit";

// Base greeting font size (px, in 1920×1080 stage space) and the smallest it
// may shrink to when a long patient name would otherwise overflow the column.
const GREETING_BASE_PX = 118;
const GREETING_MIN_PX = 64;

const EMPTY = {
  appointmentId: null,
  fullNameTh: "",
  fullNameEn: null,
  nickname: null,
  age: null,
  treatmentTitle: null,
  doctorName: null,
  durationMinutes: null,
  roomName: null,
  dateText: "",
  clinicName: "",
  clinicNameEn: null,
  clinicLogoUrl: null,
  clinicPhone: "",
  allergies: "",
  conditions: "",
};

export default {
  name: "PatientWelcomeDisplay",
  data() {
    const initial = readLastQueue() || {};
    return {
      // Live queue relayed from the dashboard. `selectedId === null` shows the
      // general welcome screen; otherwise it is a queue appointmentId.
      queue: initial.patients || [],
      clinic: initial.clinic || {},
      selectedId: "activeId" in initial ? initial.activeId : null,
      unsubscribe: null,
      // "welcome" board vs "postop" care instructions — toggled on-screen by
      // the doctor via the auto-hiding control. Resets to welcome per patient.
      mode: "welcome",
      postOpList: POSTOP_TREATMENTS,
      postOpKey: POSTOP_TREATMENTS[0].key,
      themePresets: THEME_PRESETS,
      theme: loadTheme(),
      // Per-device display language ("th" | "en"), chosen in the gear panel and
      // persisted to localStorage. Independent of the app's vue-i18n locale.
      lang: loadLang(),
      panelOpen: false,
      ctrlIdle: false,
      idleTimer: null,
      // Rotating welcome copy — each starts on a random line and picks a fresh
      // random line on a timer, independently, so the screen stays varied.
      // Seeded here (not in mounted) so the first paint is already random and
      // doesn't cross-fade away from a default line on load.
      // Bilingual rotation pools ({ th, en } parallel arrays). The index is
      // shared across languages, so switching language keeps the same line.
      careTips: CARE_TIPS,
      welcomeLines: WELCOME_LINES,
      tipIndex: Math.floor(Math.random() * CARE_TIPS.th.length),
      welcomeIndex: Math.floor(Math.random() * WELCOME_LINES.th.length),
      rotateTimer: null,
    };
  },
  computed: {
    queueIds() {
      return this.queue.map((p) => p.appointmentId);
    },
    // The patient payload for the current selection, or null on the general
    // screen (selectedId null, or a selection no longer in the queue).
    current() {
      return (
        this.queue.find((p) => p.appointmentId === this.selectedId) || null
      );
    },
    hasPatient() {
      return !!this.current;
    },
    // The general (no-patient) welcome screen. Drives hiding the top-left brand
    // so the big centered logo + clinic name can act as the hero instead.
    isGeneral() {
      return this.mode === "welcome" && !this.hasPatient;
    },
    // Active payload bound throughout the template. Falls back to a clinic-only
    // payload so the header branding still renders on the general screen.
    p() {
      return (
        this.current || {
          ...EMPTY,
          clinicName: this.clinic.clinicName || "",
          clinicNameEn: this.clinic.clinicNameEn || null,
          clinicLogoUrl: this.clinic.clinicLogoUrl || null,
        }
      );
    },
    showAlert() {
      return !!(this.p.allergies || this.p.conditions);
    },
    // Active UI label map for the current language. All static on-screen labels
    // read from here (see welcomeStrings.js).
    S() {
      return WELCOME_STRINGS[this.lang];
    },
    // Greeting name by language. Thai prefers the warm short nickname; English
    // prefers the romanized full name, falling back to the Thai name.
    greetingName() {
      if (this.lang === "en") {
        return this.p.fullNameEn || this.p.fullNameTh;
      }
      return this.p.nickname ? "คุณ" + this.p.nickname : this.p.fullNameTh;
    },
    // Supplementary name shown on the subline — the "other script" name, so it
    // doesn't simply repeat the greeting. Empty when it would duplicate it.
    secondaryName() {
      if (this.lang === "en") {
        return this.p.fullNameEn ? this.p.fullNameTh : "";
      }
      return this.p.fullNameEn || "";
    },
    // "อายุ N ปี" / "Age N" — empty when no age is on file.
    ageText() {
      if (this.p.age == null) return "";
      return this.lang === "en"
        ? `Age ${this.p.age}`
        : `อายุ ${this.p.age} ปี`;
    },
    // "ใช้เวลาประมาณ N นาที" / "Approx. N min" — empty when no duration.
    durationText() {
      if (!this.p.durationMinutes) return "";
      return this.lang === "en"
        ? `Approx. ${this.p.durationMinutes} min`
        : `ใช้เวลาประมาณ ${this.p.durationMinutes} นาที`;
    },
    // Clinic name by language. English uses the romanized name when available,
    // falling back to the Thai name so the header is never blank.
    clinicName() {
      if (this.lang === "en") {
        return this.p.clinicNameEn || this.p.clinicName || "";
      }
      return this.p.clinicName || "";
    },
    brandInitial() {
      return (this.clinicName || "").trim().charAt(0) || "·";
    },
    // Clinic slogan shown under the brand name (header) and as the sub line on
    // the general welcome screen. Translated via the active label map.
    tagline() {
      return this.S.tagline;
    },
    // The selected post-op treatment, resolved to the active language so the
    // template binds plain strings/arrays.
    activePostOp() {
      const t = getPostOp(this.postOpKey) || POSTOP_TREATMENTS[0];
      const lang = this.lang;
      return {
        label: t.label[lang],
        dos: t.dos[lang],
        donts: t.donts[lang],
        notes: t.notes[lang],
      };
    },
    boardStyle() {
      return { background: this.theme.bg, color: this.theme.text };
    },
    // The native color input needs a hex value; fall back when the active
    // background is a gradient/non-hex preset.
    bgHex() {
      return /^#/.test(this.theme.bg) ? this.theme.bg : "#eaf3f3";
    },
    currentTip() {
      return this.careTips[this.lang][this.tipIndex];
    },
    currentWelcomeLine() {
      return this.welcomeLines[this.lang][this.welcomeIndex];
    },
  },
  watch: {
    // Whenever the selection changes (arrow navigation or a removed patient
    // falling back to the general screen), land on the welcome board and
    // refresh the rotating copy. Realtime refreshes that keep the same
    // selection don't fire this, so the doctor's post-op view isn't disturbed.
    selectedId() {
      this.mode = "welcome";
      this.rotateCopy();
      this.fitGreeting();
    },
  },
  mounted() {
    document.title = "Patient Welcome";
    this.unsubscribe = subscribeWelcomeQueue((msg) => this.applyMessage(msg));
    this.fit();
    this.fitGreeting();
    window.addEventListener("resize", this.fit);
    window.addEventListener("mousemove", this.pokeCtrl);
    window.addEventListener("click", this.onDocClick);
    window.addEventListener("keydown", this.onKeydown);
    this.pokeCtrl();
    // Rotate the tip + welcome line every 10 minutes, each to a fresh random
    // line, with a cross-fade (see template). Initial lines are seeded in data().
    this.rotateTimer = setInterval(this.rotateCopy, 10 * 60 * 1000);
  },
  beforeDestroy() {
    if (this.unsubscribe) this.unsubscribe();
    window.removeEventListener("resize", this.fit);
    window.removeEventListener("mousemove", this.pokeCtrl);
    window.removeEventListener("click", this.onDocClick);
    window.removeEventListener("keydown", this.onKeydown);
    clearTimeout(this.idleTimer);
    clearInterval(this.rotateTimer);
  },
  methods: {
    // Apply a relayed queue message. An explicit `activeId` (open / row click)
    // sets the selection; realtime refreshes omit it so the current pick is
    // kept, then reconciled away if that patient has left the queue.
    applyMessage(msg) {
      if (!msg) return;
      this.queue = msg.patients || [];
      this.clinic = msg.clinic || {};
      if ("activeId" in msg) this.selectedId = msg.activeId;
      this.selectedId = reconcileSelection(this.queueIds, this.selectedId);
    },
    selectNext() {
      this.selectedId = nextSelection(this.queueIds, this.selectedId);
    },
    selectPrev() {
      this.selectedId = prevSelection(this.queueIds, this.selectedId);
    },
    onKeydown(e) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        this.selectNext();
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        this.selectPrev();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        this.cyclePostOp(1);
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        this.cyclePostOp(-1);
      }
    },
    fit() {
      const b = this.$refs.board;
      if (!b) return;
      const s = Math.min(window.innerWidth / 1920, window.innerHeight / 1080);
      b.style.transform = "scale(" + s + ")";
    },
    // Shrink the greeting so a long patient name stays on one unbroken line
    // within the left column. Measured in stage (1920-wide) space — the board's
    // CSS scale transform doesn't affect layout box sizes. Runs after render so
    // the refs are laid out; no-op when there's no patient greeting on screen.
    fitGreeting() {
      this.$nextTick(() => {
        const box = this.$refs.greeting;
        const name = this.$refs.greetingName;
        if (!box || !name) return;
        // Reset to base before measuring so we always scale from a known size.
        box.style.fontSize = GREETING_BASE_PX + "px";
        const size = fitFontSize(
          GREETING_BASE_PX,
          box.clientWidth,
          name.scrollWidth,
          GREETING_MIN_PX
        );
        box.style.fontSize = size + "px";
      });
    },
    setMode(m) {
      this.mode = m;
      this.pokeCtrl();
    },
    selectPostOp(key) {
      this.postOpKey = key;
      this.mode = "postop";
      this.pokeCtrl();
    },
    // ArrowRight/ArrowLeft switch post-op care screens. The first press from a
    // non-post-op screen just enters post-op mode (showing the current
    // treatment); subsequent presses cycle through treatments with wrap-around.
    cyclePostOp(dir) {
      if (this.mode !== "postop") {
        this.mode = "postop";
        this.pokeCtrl();
        return;
      }
      const keys = this.postOpList.map((t) => t.key);
      this.postOpKey =
        dir > 0
          ? nextPostOp(keys, this.postOpKey)
          : prevPostOp(keys, this.postOpKey);
      this.pokeCtrl();
    },
    togglePanel() {
      this.panelOpen = !this.panelOpen;
      this.pokeCtrl();
    },
    // Close the panel when clicking anywhere outside it (the gear toggles itself).
    onDocClick(e) {
      if (!this.panelOpen) return;
      const panel = this.$refs.panel;
      const gear = this.$refs.gear;
      if (panel && panel.contains(e.target)) return;
      if (gear && gear.contains(e.target)) return;
      this.panelOpen = false;
    },
    selectPreset(p) {
      this.theme = { key: p.key, bg: p.bg, text: p.text };
      saveTheme(this.theme);
    },
    onBgInput(e) {
      this.theme = { key: null, bg: e.target.value, text: this.theme.text };
      saveTheme(this.theme);
    },
    onTextInput(e) {
      this.theme = { key: null, bg: this.theme.bg, text: e.target.value };
      saveTheme(this.theme);
    },
    // Pick a fresh random index in [0, len) that differs from `current`
    // (so the same line never repeats back-to-back). Falls back gracefully
    // when the list has a single entry.
    nextRandom(len, current) {
      if (len <= 1) return 0;
      let i = Math.floor(Math.random() * (len - 1));
      if (i >= current) i += 1;
      return i;
    },
    rotateCopy() {
      // Pools are parallel across languages, so either side gives the length.
      this.tipIndex = this.nextRandom(this.careTips.th.length, this.tipIndex);
      this.welcomeIndex = this.nextRandom(
        this.welcomeLines.th.length,
        this.welcomeIndex
      );
    },
    // Switch the display language and remember it on this device.
    setLang(l) {
      this.lang = l;
      saveLang(l);
      this.fitGreeting();
      this.pokeCtrl();
    },
    // Keep the doctor control visible while the mouse moves, then fade it out
    // so the patient sees a clean board.
    pokeCtrl() {
      this.ctrlIdle = false;
      clearTimeout(this.idleTimer);
      this.idleTimer = setTimeout(() => {
        this.ctrlIdle = true;
      }, 3000);
    },
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Noto+Sans+Thai:wght@400;500;600;700&family=Noto+Serif+Thai:wght@400;500;600;700&display=swap");

.welcome-stage {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #dfeaea;
  overflow: hidden;
}
.welcome-board {
  position: relative;
  width: 1920px;
  height: 1080px;
  flex: none;
  transform-origin: center center;
  overflow: hidden;
  background: linear-gradient(155deg, #e9f3f4 0%, #f4f1ea 58%, #eaf4f4 100%);
  font-family: "Noto Sans Thai", sans-serif;
  color: #234b50;
}

/* decorative floating blobs */
.blob {
  position: absolute;
  border-radius: 50%;
}
.blob-top {
  top: -240px;
  right: -180px;
  width: 660px;
  height: 660px;
  background: radial-gradient(circle, #d6ebec 0%, rgba(214, 235, 236, 0) 70%);
  animation: floatB 24s ease-in-out infinite;
}
.blob-bottom {
  bottom: -280px;
  left: -200px;
  width: 680px;
  height: 680px;
  background: radial-gradient(circle, #f4e6d4 0%, rgba(244, 230, 212, 0) 70%);
  animation: floatA 28s ease-in-out infinite;
}

.board-inner {
  position: relative;
  z-index: 1;
  width: 100%;
  height: 100%;
  padding: 72px 80px;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  /* Slow whole-content orbit so no pixel stays lit identically (burn-in). */
  animation: drift 80s ease-in-out infinite;
}

/* Header */
.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.brand {
  display: flex;
  align-items: center;
  gap: 20px;
}
.brand-logo {
  width: 110px;
  height: 110px;
  border-radius: 28px;
  object-fit: contain;
  box-shadow: 0 10px 24px rgba(47, 143, 153, 0.28);
}
.brand-mark {
  width: 110px;
  height: 110px;
  border-radius: 28px;
  background: #2f8f99;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-family: "Noto Serif Thai", serif;
  font-size: 48px;
  font-weight: 600;
  box-shadow: 0 10px 24px rgba(47, 143, 153, 0.28);
}
.brand-name {
  font-size: 27px;
  font-weight: 700;
  color: #234b50;
  letter-spacing: 0.2px;
}
.brand-tagline {
  font-size: 16px;
  color: #6a8388;
  letter-spacing: 0.3px;
  margin-top: 3px;
}
.meta {
  display: flex;
  align-items: center;
  gap: 14px;
}
.meta-pill {
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 13px 24px;
  background: rgba(255, 255, 255, 0.72);
  border: 1px solid rgba(47, 143, 153, 0.16);
  border-radius: 999px;
  font-size: 18px;
  font-weight: 600;
  color: #3a6b70;
}
.meta-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: #2f8f99;
}

/* Body */
.body {
  display: flex;
  gap: 44px;
  flex: 1;
  margin-top: 52px;
  min-height: 0;
}
.left {
  flex: 1.55;
  display: flex;
  flex-direction: column;
  /* Let the column shrink to its flex width even with the nowrap greeting name;
     the name overflows (then auto-shrinks via fitGreeting) instead of widening
     the column and squeezing the right-hand cards. */
  min-width: 0;
}
.eyebrow {
  display: flex;
  align-items: center;
  gap: 14px;
  font-size: 20px;
  font-weight: 700;
  letter-spacing: 3px;
  color: #2f8f99;
}
.eyebrow-line {
  width: 42px;
  height: 3px;
  background: #2f8f99;
  border-radius: 2px;
}
.greeting {
  font-family: "Noto Serif Thai", serif;
  font-size: 118px;
  line-height: 1.08;
  font-weight: 600;
  color: #234b50;
  margin-top: 24px;
}
.greeting-name {
  color: #2f8f99;
  /* Thai has no spaces, so the browser would break a long name mid-word.
     Keep the whole name on one unbroken line; the font auto-shrinks (see
     fitGreeting) so even long names fit the column. */
  display: inline-block;
  white-space: nowrap;
}
.subline {
  font-size: 33px;
  color: #41666b;
  margin-top: 18px;
}
.subline-strong {
  font-weight: 700;
}
.welcome-copy {
  font-size: 26px;
  line-height: 1.6;
  color: #5e7479;
  max-width: 820px;
  margin-top: 30px;
}
.spacer {
  flex: 1;
}
.care-tip {
  display: flex;
  gap: 24px;
  align-items: flex-start;
  background: rgba(255, 255, 255, 0.66);
  border: 1px solid rgba(47, 143, 153, 0.12);
  border-radius: 28px;
  padding: 30px 36px;
  max-width: 860px;
  animation: floatTip 22s ease-in-out infinite;
}
.care-quote {
  flex: 0 0 auto;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: #fbe7df;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: "Noto Serif Thai", serif;
  font-size: 56px;
  line-height: 1;
  color: #d8826b;
  padding-top: 18px;
  box-sizing: border-box;
}
.care-label {
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 2px;
  color: #d8826b;
  margin-bottom: 10px;
}
.care-text {
  font-size: 25px;
  line-height: 1.55;
  color: #3f5b5f;
  font-family: "Noto Serif Thai", serif;
}

/* Right column cards */
.right {
  width: 580px;
  /* Never let a long greeting name in the left column squeeze the cards. */
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 24px;
}
.card {
  background: #ffffff;
  border-radius: 28px;
  padding: 34px;
  box-shadow: 0 18px 44px rgba(35, 75, 80, 0.09);
  border: 1px solid rgba(47, 143, 153, 0.06);
  animation: floatCard 18s ease-in-out infinite;
}
.card-eyebrow {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 2px;
  color: #2f8f99;
}
.dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
}
.dot-teal {
  background: #2f8f99;
}
.card-title {
  font-family: "Noto Serif Thai", serif;
  font-size: 40px;
  line-height: 1.2;
  color: #234b50;
  margin-top: 18px;
  font-weight: 600;
}
.card-sub {
  font-size: 21px;
  color: #6a8388;
  margin-top: 14px;
}
.duration-pill {
  margin-top: 24px;
  display: inline-flex;
  align-items: center;
  gap: 12px;
  background: #e3f0f1;
  border-radius: 999px;
  padding: 14px 26px;
  font-size: 22px;
  font-weight: 700;
  color: #2f8f99;
}

/* Medical alert */
.alert-card {
  background: #fdf4e6;
  border: 1px solid rgba(201, 138, 55, 0.26);
  border-radius: 28px;
  padding: 28px 32px;
  animation: floatAlert 20s ease-in-out infinite;
}
.alert-head {
  display: flex;
  align-items: center;
  gap: 16px;
}
.alert-icon {
  flex: 0 0 auto;
  width: 54px;
  height: 54px;
  border-radius: 50%;
  background: #f6e1bd;
  display: flex;
  align-items: center;
  justify-content: center;
}
.alert-title {
  font-size: 21px;
  font-weight: 700;
  color: #a9712a;
  letter-spacing: 1px;
}
.alert-cols {
  display: flex;
  gap: 24px;
  margin-top: 22px;
}
.alert-col {
  flex: 1;
}
.alert-key {
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 1px;
  color: #b98a47;
  margin-bottom: 6px;
}
.alert-val {
  font-size: 24px;
  font-weight: 600;
  color: #7a5a24;
}
.alert-divider {
  width: 1px;
  background: rgba(201, 138, 55, 0.3);
}

/* ---------- General welcome / thankyou screen (no patient) ---------- */
.welcome-general {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 36px;
  min-height: 0;
}
.wg-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  max-width: 1200px;
}
/* Greeting at the very top of the general screen. */
.wg-hello {
  font-family: "Noto Sans Thai", sans-serif;
  font-size: 52px;
  font-weight: 600;
  color: #2f8f99;
  letter-spacing: 1px;
  margin-bottom: 34px;
}
/* Big centered logo (hero) — the top-left brand is hidden on this screen. */
.wg-logo {
  width: 240px;
  height: 240px;
  border-radius: 48px;
  object-fit: contain;
  box-shadow: 0 16px 38px rgba(47, 143, 153, 0.28);
  margin-bottom: 26px;
}
.wg-mark {
  width: 240px;
  height: 240px;
  border-radius: 48px;
  background: #2f8f99;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-family: "Noto Serif Thai", serif;
  font-size: 104px;
  font-weight: 600;
  box-shadow: 0 16px 38px rgba(47, 143, 153, 0.28);
  margin-bottom: 26px;
}
.wg-title {
  font-family: "Noto Serif Thai", serif;
  /* Holds the clinic name (longer than the old fixed greeting), so cap the
     width and let long names wrap to two balanced lines instead of clipping. */
  font-size: 104px;
  line-height: 1.12;
  font-weight: 600;
  color: #234b50;
  margin-top: 22px;
  max-width: 1400px;
  word-break: break-word;
}
.wg-welcome {
  font-family: "Noto Serif Thai", serif;
  font-size: 40px;
  font-weight: 500;
  color: #2f8f99;
  margin-top: 24px;
}
.wg-copy {
  font-size: 29px;
  line-height: 1.6;
  color: #5e7479;
  max-width: 1000px;
  margin-top: 30px;
}
.welcome-general .wg-tip {
  margin-top: 48px;
  text-align: left;
}

/* Animations */
.rise {
  animation: riseIn 0.8s ease both;
}
.rise-1 {
  animation-duration: 0.9s;
  animation-delay: 0.12s;
}
.rise-2 {
  animation-duration: 0.9s;
  animation-delay: 0.24s;
}
@keyframes riseIn {
  from {
    transform: translateY(26px);
    opacity: 0;
  }
  to {
    transform: none;
    opacity: 1;
  }
}
@keyframes floatA {
  0%,
  100% {
    transform: translate(0, 0);
  }
  50% {
    transform: translate(-18px, 16px);
  }
}
@keyframes floatB {
  0%,
  100% {
    transform: translate(0, 0);
  }
  50% {
    transform: translate(16px, -14px);
  }
}

/* Very slow, tiny content orbit — keeps the board from burning in without
   being perceptible as motion. */
@keyframes drift {
  0% {
    transform: translate(0, 0);
  }
  25% {
    transform: translate(5px, -3px);
  }
  50% {
    transform: translate(0, 5px);
  }
  75% {
    transform: translate(-5px, -3px);
  }
  100% {
    transform: translate(0, 0);
  }
}

/* Per-element drift — gentle vertical bob only (single axis reads as calm,
   not dizzy), small amplitude, long period. */
@keyframes floatCard {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-5px);
  }
}
@keyframes floatAlert {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(4px);
  }
}
@keyframes floatTip {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-4px);
  }
}

/* Cross-fade for the rotating tip + welcome line (rotates every 10 min). */
.fade-swap-enter-active,
.fade-swap-leave-active {
  transition: opacity 0.6s ease, transform 0.6s ease;
}
.fade-swap-enter {
  opacity: 0;
  transform: translateY(12px);
}
.fade-swap-leave-to {
  opacity: 0;
  transform: translateY(-12px);
}

/* Respect reduced-motion: stop the continuous drift/floats, keep a calm
   opacity-only swap for the rotating text. */
@media (prefers-reduced-motion: reduce) {
  .board-inner,
  .blob-top,
  .blob-bottom,
  .card,
  .alert-card,
  .care-tip {
    animation: none;
  }
  .fade-swap-enter-active,
  .fade-swap-leave-active {
    transition: opacity 0.6s ease;
  }
  .fade-swap-enter,
  .fade-swap-leave-to {
    transform: none;
  }
}

/* ---------- Post-op care mode ---------- */
.po-titlebar {
  margin-top: 44px;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  animation: riseIn 0.9s ease both;
}
.po-eyebrow {
  display: flex;
  align-items: center;
  gap: 14px;
  font-size: 20px;
  font-weight: 700;
  letter-spacing: 3px;
  color: #2f8f99;
}
.po-eyebrow-line {
  width: 42px;
  height: 3px;
  background: #2f8f99;
  border-radius: 2px;
}
.po-title {
  font-family: "Noto Serif Thai", serif;
  font-size: 80px;
  font-weight: 600;
  line-height: 1.05;
  margin-top: 14px;
  color: #234b50;
}
.po-for {
  text-align: right;
}
.po-for-name {
  font-size: 26px;
  color: #41666b;
}
.po-for-name span {
  font-weight: 700;
  color: #234b50;
}
.po-for-date {
  font-size: 20px;
  color: #7e9398;
  margin-top: 6px;
}

.po-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 28px;
  flex: 1;
  margin-top: 38px;
  min-height: 0;
  animation: riseIn 0.95s ease both;
}
.po-card {
  border-radius: 28px;
  padding: 34px 36px;
  display: flex;
  flex-direction: column;
}
.po-card--do {
  background: #eaf6ee;
  border: 1px solid rgba(46, 160, 93, 0.22);
}
.po-card--avoid {
  background: #fdeeee;
  border: 1px solid rgba(201, 55, 55, 0.2);
}
.po-card--note {
  background: #e4f0f1;
  border: 1px solid rgba(47, 143, 153, 0.22);
}
.po-card-head {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 26px;
}
.po-card-icon {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}
.po-card--do .po-card-icon {
  background: #d4ecdd;
}
.po-card--avoid .po-card-icon {
  background: #f6d9d9;
}
.po-card--note .po-card-icon {
  background: #cfe6e8;
}
.po-card-title {
  font-size: 28px;
  font-weight: 700;
}
.po-card--do .po-card-title {
  color: #1f7a45;
}
.po-card--avoid .po-card-title {
  color: #b53636;
}
.po-card--note .po-card-title {
  color: #2f8f99;
}
.po-items {
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.po-item {
  display: flex;
  gap: 14px;
  align-items: flex-start;
}
.po-item-ic {
  flex: 0 0 auto;
  margin-top: 2px;
}
.po-item span {
  font-size: 24px;
  line-height: 1.4;
  color: #2c4146;
}
.po-item--note .po-bullet {
  flex: 0 0 auto;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: #2f8f99;
  margin-top: 11px;
}
.po-spacer {
  flex: 1;
}
.po-contact {
  margin-top: 24px;
  padding: 18px 22px;
  background: rgba(255, 255, 255, 0.6);
  border-radius: 18px;
  font-size: 21px;
  color: #3f5b5f;
}
.po-contact span {
  font-weight: 700;
  color: #2f8f99;
}

/* ---------- Doctor control (gear + panel) ---------- */
.dc-gear {
  position: fixed;
  right: 22px;
  bottom: 22px;
  z-index: 60;
  width: 58px;
  height: 58px;
  border-radius: 50%;
  border: none;
  background: #234b50;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 8px 22px rgba(0, 0, 0, 0.28);
  transition: opacity 0.4s ease;
}
.dc-gear--idle {
  opacity: 0;
  pointer-events: none;
}

/* Very subtle queue navigation arrows (mirror the Up/Down keys). Barely-there
   by default; gain presence only on hover. */
.dc-nav {
  position: fixed;
  right: 18px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 55;
  display: flex;
  flex-direction: column;
  gap: 8px;
  opacity: 0.32;
  transition: opacity 0.4s ease;
}
.dc-nav:hover {
  opacity: 0.9;
}
.dc-nav--idle {
  opacity: 0;
  pointer-events: none;
}
.dc-nav-btn {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  border: none;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.15s ease;
}
.dc-nav-btn:hover {
  background: rgba(35, 75, 80, 0.18);
}
.dc-nav-btn:active {
  transform: scale(0.9);
}

.dc-panel {
  position: fixed;
  right: 22px;
  bottom: 92px;
  z-index: 60;
  width: 380px;
  background: #fff;
  border-radius: 22px;
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.25);
  padding: 22px;
  font-family: "Noto Sans Thai", sans-serif;
  color: #234b50;
}
.dc-section {
  margin-bottom: 20px;
}
.dc-section:last-child {
  margin-bottom: 0;
}
.dc-label {
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: #6a8388;
  margin-bottom: 10px;
}
.dc-seg {
  display: flex;
  background: #eef3f3;
  border-radius: 14px;
  padding: 5px;
  gap: 4px;
}
.dc-seg-btn {
  flex: 1;
  border: none;
  background: transparent;
  padding: 12px;
  border-radius: 10px;
  font-family: inherit;
  font-size: 16px;
  font-weight: 600;
  color: #5e7479;
  cursor: pointer;
  transition: all 0.2s ease;
}
.dc-seg-btn--active {
  background: #234b50;
  color: #fff;
}
.dc-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}
.dc-grid-btn {
  border: 1px solid #dbe6e6;
  background: #fff;
  border-radius: 12px;
  min-height: 64px;
  padding: 10px 8px;
  font-family: inherit;
  font-size: 15px;
  font-weight: 600;
  color: #3a6b70;
  cursor: pointer;
  transition: all 0.2s ease;
}
.dc-grid-btn--active {
  border-color: #2f8f99;
  background: #e3f0f1;
  color: #1f6168;
}
.dc-presets {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.dc-preset {
  min-width: 56px;
  height: 44px;
  padding: 0 10px;
  border-radius: 11px;
  border: 2px solid transparent;
  font-family: inherit;
  font-weight: 700;
  font-size: 14px;
  cursor: pointer;
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.06);
}
.dc-preset--active {
  border-color: #234b50;
}
.dc-colors {
  display: flex;
  gap: 18px;
  margin-top: 14px;
}
.dc-colors label {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  color: #5e7479;
}
.dc-colors input[type="color"] {
  width: 44px;
  height: 32px;
  border: 1px solid #dbe6e6;
  border-radius: 8px;
  background: none;
  padding: 0;
  cursor: pointer;
}
</style>
